import { useCallback, useMemo } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Box } from '@givebutter/primitives/box';
import { BoxIcon } from '@givebutter/primitives/box-icon';
import { maskDecimal, maskWholeNumber, pluralize } from '@givebutter/util';
import {
    faAddressCard,
    faGift,
    faMailbox,
    faMoneyBillTransfer,
    faPrint,
    faQrcode,
    faStop,
    faTruck,
    faTruckFast,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchLetterFulfillmentStats, fetchLetterMetrics } from 'api/engage';
import * as TrackingStatus from 'assets/js/pages/engage/letters/LetterEasySendTrackingStatus';
import { useCurrentLetter } from 'assets/js/pages/engage/letters/useCurrentLetter';
import { useCurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { capitalize } from 'lodash';
import { accountQueryKeys } from 'queries/account-query-keys';
import { LetterAggregatedFulfillmentStatus } from 'types/domain';

const FulfillmentStatusToStep: { [key in LetterAggregatedFulfillmentStatus]: number } = {
    Received: 0,
    'In Production': 1,
    Mailed: 2,
    'Processed for Delivery': 3,
};

const LetterEasySendDeliveryStats = () => {
    const currencySymbol = useCurrencySymbol();
    const { accountId } = useCurrentAccount();
    const { data: letter } = useCurrentLetter();
    const { data: metricsData } = useSuspenseQuery({
        queryKey: accountQueryKeys.engageMessageMetrics(accountId, letter.id),
        queryFn: () => fetchLetterMetrics(accountId, letter.id),
    });
    const metrics = metricsData.metrics;
    const { data } = useSuspenseQuery({
        queryKey: accountQueryKeys.engageMessageFulfillmentStats(accountId, letter.id),
        queryFn: () => fetchLetterFulfillmentStats(accountId, letter.id),
    });
    const stats = data.stats;
    const fulfillmentStatus = data.current_status;
    const recipientCount = letter.recipient_count;
    const activeStep = getStepFromAggregatedStatus(fulfillmentStatus) ?? 0;
    const providerProps = useMemo(() => ({ activeStep }), [activeStep]);
    const getMetricPercentage = useCallback(
        (value: number) => parseFloat(((value / recipientCount) * 100).toFixed(2)),
        [recipientCount],
    );
    const [deliveredPercent, reRoutedPercent, returnedToSenderPercent, conversionRatePercent] = [
        stats.delivered,
        stats.re_routed,
        stats.returned_to_sender,
        metrics.total_donations,
    ].map(getMetricPercentage);

    return (
        <Box className={'flex flex-col gap-4 p-6'}>
            <p className={'text-xl font-medium'}>Tracking</p>

            <TrackingStatus.Provider value={providerProps}>
                <div className={'flex py-4'}>
                    <TrackingStatus.Root step={getStepFromAggregatedStatus('Received')}>
                        <TrackingStatus.Icon icon={faAddressCard} />
                        <TrackingStatus.DescriptionContainer>
                            <TrackingStatus.DescriptionTitle>
                                Received
                            </TrackingStatus.DescriptionTitle>
                            <TrackingStatus.DescriptionSubtitle>
                                Order has been received
                            </TrackingStatus.DescriptionSubtitle>
                        </TrackingStatus.DescriptionContainer>
                    </TrackingStatus.Root>
                    <TrackingStatus.Root step={getStepFromAggregatedStatus('In Production')}>
                        <TrackingStatus.Icon icon={faPrint} />
                        <TrackingStatus.DescriptionContainer>
                            <TrackingStatus.DescriptionTitle>
                                In production
                            </TrackingStatus.DescriptionTitle>
                            <TrackingStatus.DescriptionSubtitle>
                                {capitalize(letter.settings.page_type)}s are being printed and
                                stamped
                            </TrackingStatus.DescriptionSubtitle>
                        </TrackingStatus.DescriptionContainer>
                    </TrackingStatus.Root>
                    <TrackingStatus.Root step={getStepFromAggregatedStatus('Mailed')}>
                        <TrackingStatus.Icon icon={faTruckFast} />
                        <TrackingStatus.DescriptionContainer>
                            <TrackingStatus.DescriptionTitle>
                                Mailed
                            </TrackingStatus.DescriptionTitle>
                            <TrackingStatus.DescriptionSubtitle>
                                {capitalize(letter.settings.page_type)}s have been been sent via
                                USPS
                            </TrackingStatus.DescriptionSubtitle>
                        </TrackingStatus.DescriptionContainer>
                    </TrackingStatus.Root>
                    <TrackingStatus.Root
                        step={getStepFromAggregatedStatus('Processed for Delivery')}
                    >
                        <TrackingStatus.Icon icon={faMailbox} />
                        <TrackingStatus.DescriptionContainer>
                            <TrackingStatus.DescriptionTitle>
                                Out for delivery
                            </TrackingStatus.DescriptionTitle>
                            <TrackingStatus.DescriptionSubtitle>
                                {capitalize(letter.settings.page_type)}s have started being
                                delivered
                            </TrackingStatus.DescriptionSubtitle>
                        </TrackingStatus.DescriptionContainer>
                    </TrackingStatus.Root>
                </div>
            </TrackingStatus.Provider>

            <div className="flex gap-[3.75rem] rounded-lg bg-gray-50 p-6">
                <div className="flex flex-col gap-1">
                    <div className="text-gray-500">Sent to</div>
                    <div className="whitespace-nowrap">
                        <span className="font-medium">
                            {maskWholeNumber(letter.recipient_count)}
                        </span>{' '}
                        {pluralize(letter.recipient_count, 'recipient')}
                    </div>
                </div>
                <div className="grid w-full gap-8 sm:grid-cols-2 lg:grid-cols-3">
                    <div className="flex gap-3">
                        <BoxIcon size="lg">
                            <FontAwesomeIcon
                                className={'!text-green-600'}
                                size="lg"
                                icon={faMailbox}
                            />
                        </BoxIcon>
                        <div>
                            <div className="flex items-center gap-1.5">
                                <span className="text-lg font-medium text-green-600">
                                    {deliveredPercent}%
                                </span>
                                <span className="text-sm text-gray-500">
                                    ({maskWholeNumber(stats.delivered)})
                                </span>
                            </div>
                            <div className="text-sm font-medium text-gray-600">Delivered</div>
                        </div>
                    </div>
                    <div className="flex gap-3">
                        <BoxIcon size="lg">
                            <FontAwesomeIcon
                                className={'!text-orange-600'}
                                size="lg"
                                icon={faTruck}
                            />
                        </BoxIcon>
                        <div>
                            <div className="flex items-center gap-1.5">
                                <span className="text-lg font-medium text-orange-600">
                                    {reRoutedPercent}%
                                </span>
                                <span className="text-sm text-gray-500">
                                    ({maskWholeNumber(stats.re_routed)})
                                </span>
                            </div>
                            <div className="text-sm font-medium text-gray-600">Rerouted</div>
                        </div>
                    </div>
                    <div className="flex gap-3">
                        <BoxIcon size="lg">
                            <FontAwesomeIcon className={'!text-red-600'} size="lg" icon={faStop} />
                        </BoxIcon>
                        <div>
                            <div className="flex items-center gap-1.5">
                                <span className="text-lg font-medium text-red-600">
                                    {returnedToSenderPercent}%
                                </span>
                                <span className="text-sm text-gray-500">
                                    ({maskWholeNumber(stats.returned_to_sender)})
                                </span>
                            </div>
                            <div className="text-sm font-medium text-gray-600">
                                Returned to sender
                            </div>
                        </div>
                    </div>
                    {letter.has_trackable_qr_codes && (
                        <>
                            <div className="flex gap-3">
                                <BoxIcon size="lg">
                                    <FontAwesomeIcon
                                        className={'!text-blue-600'}
                                        size="lg"
                                        icon={faQrcode}
                                    />
                                </BoxIcon>
                                <div>
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-lg font-medium text-blue-600">
                                            {maskWholeNumber(metrics.qr_code_scans)}
                                        </span>
                                    </div>
                                    <div className="text-sm font-medium text-gray-600">
                                        Unique QR code scans
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-3">
                                <BoxIcon size="lg">
                                    <FontAwesomeIcon size="lg" icon={faGift} />
                                </BoxIcon>
                                <div>
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-brand-violet-primary text-lg font-medium">
                                            {currencySymbol}
                                            {maskDecimal(metrics.revenue_generated)}
                                        </span>
                                        {metrics.total_donations !== 0 && (
                                            <span className="text-sm text-gray-500">
                                                ({maskWholeNumber(metrics.total_donations)}){' '}
                                                {pluralize(metrics.total_donations, 'supporter')}
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-sm font-medium text-gray-600">
                                        Revenue generated
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-3">
                                <BoxIcon size="lg">
                                    <FontAwesomeIcon
                                        className={'!text-green-600'}
                                        size="lg"
                                        icon={faMoneyBillTransfer}
                                    />
                                </BoxIcon>
                                <div>
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-lg font-medium text-green-600">
                                            {conversionRatePercent}%
                                        </span>
                                        {metrics.total_donations !== 0 && (
                                            <span className="text-sm text-gray-500">
                                                ({maskWholeNumber(metrics.total_donations)}){' '}
                                                {pluralize(metrics.total_donations, 'supporter')}
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-sm font-medium text-gray-600">
                                        Conversion rate
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </Box>
    );
};

const getStepFromAggregatedStatus = (status: LetterAggregatedFulfillmentStatus) =>
    FulfillmentStatusToStep[status];

export { LetterEasySendDeliveryStats };
